import { FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";

import { faFlask, faSlidersH } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components/Icon/Icon";

import { AiSnippetEnum } from "@/enum/ai-enrichment.enum";

import { FewShotExample } from "../schema/aiSnippet";

import { ResponseTypeRadios } from "./ResponseTypeRadios";
import { FewShotExamples } from "./FewShotExamples";

interface SnippetConfigPaneProps {
	promptType: AiSnippetEnum;
	allowedValues: string[];
	fewShotExamples: FewShotExample[];
	canPrefillFewShotFromLeads: boolean;
	onChangePromptType: (type: AiSnippetEnum) => void;
	onChangeAllowedValues: (values: string[]) => void;
	onChangeFewShotExamples: (examples: FewShotExample[]) => void;
	onPrefillFewShotFromLeads: () => void;
}

export const SnippetConfigPane: FunctionComponent<SnippetConfigPaneProps> = ({
	promptType,
	allowedValues,
	fewShotExamples,
	canPrefillFewShotFromLeads,
	onChangePromptType,
	onChangeAllowedValues,
	onChangeFewShotExamples,
	onPrefillFewShotFromLeads,
}) => {
	const { t } = useTranslation();
	const [explainerOpen, setExplainerOpen] = useState<boolean>(false);

	return (
		<div className="flex-1 min-w-0 bg-[#FAFBFC] flex flex-col overflow-hidden">
			<div className="flex-1 overflow-y-auto px-5.5 pt-5.5 pb-4 min-h-0">
				<div className="bg-white border border-border-light rounded-xl p-3.5 mb-3">
					<div className="flex items-center gap-1.5 mb-2.5 text-[12.5px] font-semibold text-text">
						<Icon className="w-3 h-3 text-text-meta" icon={faSlidersH} />
						<span>{t("aiSnippet.modal.responseType.title")}</span>
					</div>
					<ResponseTypeRadios
						allowedValues={allowedValues}
						promptType={promptType}
						onChangeAllowedValues={onChangeAllowedValues}
						onChangePromptType={onChangePromptType}
					/>
				</div>

				<FewShotExamples
					canPrefillFromLeads={canPrefillFewShotFromLeads}
					examples={fewShotExamples}
					onChange={onChangeFewShotExamples}
					onPrefillFromLeads={onPrefillFewShotFromLeads}
				/>
			</div>

			<div className="flex-shrink-0 border-t border-border-light bg-white relative">
				<button
					className="w-full flex items-center gap-1.5 px-4 py-2.5 bg-transparent border-0 text-xs text-text-meta cursor-pointer hover:bg-background-light hover:text-text transition-colors"
					type="button"
					onMouseEnter={() => setExplainerOpen(true)}
					onMouseLeave={() => setExplainerOpen(false)}
					onClick={() => setExplainerOpen((o) => !o)}
				>
					<Icon className="w-3 h-3 text-blue-action" icon={faFlask} />
					<span>{t("aiSnippet.modal.testExplainer.trigger")}</span>
				</button>

				{explainerOpen && (
					<div
						className="absolute left-4 right-4 bottom-[calc(100%+6px)] bg-text text-white rounded-lg px-3.5 py-3 shadow-xl z-50"
						onMouseEnter={() => setExplainerOpen(true)}
						onMouseLeave={() => setExplainerOpen(false)}
					>
						<div className="text-xs font-semibold mb-1">
							{t("aiSnippet.modal.testExplainer.title")}
						</div>
						<div className="text-[11.5px] leading-relaxed text-white/85">
							{t("aiSnippet.modal.testExplainer.body")}
						</div>
					</div>
				)}
			</div>
		</div>
	);
};
